import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { UserProfileAchievementsViewModel } from "../../types/view-models";

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchPublicUsers: builder.query<
      Array<{
        id: string;
        username: string | null;
        name: string | null;
        avatar: string | null;
        verified: boolean;
        trustScore: string | number | null;
        reviewCount: number;
      }>,
      { q: string; limit?: number }
    >({
      query: ({ q, limit }) => ({ url: "/users/search", method: "GET", params: { q, limit }, usePublic: true }),
      ...defaultQueryFreshness,
    }),
    fetchUserById: builder.query<unknown, string>({
      query: (id) => ({ url: `/users/${id}`, method: "GET" }),
      providesTags: (_res, _err, id) => [{ type: "User", id }],
      ...defaultQueryFreshness,
    }),
    fetchPublicUserByUsername: builder.query<unknown, string>({
      query: (username) => ({ url: `/users/username/${encodeURIComponent(username)}/public`, method: "GET", usePublic: true }),
      providesTags: (_res, _err, username) => [{ type: "User", id: `public-username:${username}` }],
      ...defaultQueryFreshness,
    }),
    fetchPublicUserById: builder.query<unknown, string>({
      query: (id) => ({ url: `/users/${id}/public`, method: "GET", usePublic: true }),
      providesTags: (_res, _err, id) => [{ type: "User", id: `public:${id}` }],
      ...defaultQueryFreshness,
    }),
    fetchUserAchievements: builder.query<UserProfileAchievementsViewModel, string>({
      query: (id) => ({ url: `/users/${id}/achievements`, method: "GET", usePublic: true }),
      providesTags: (_res, _err, id) => [{ type: "User", id: `achievements:${id}` }],
      ...defaultQueryFreshness,
    }),
    updateUserAvatar: builder.mutation<unknown, { userId: string; file: Blob | File }>({
      query: ({ userId, file }) => {
        const formData = new FormData();
        formData.append("file", file, file instanceof File ? file.name : "avatar.webp");
        return { url: `/users/${userId}/avatar`, method: "POST", data: formData, timeout: 30000 };
      },
      invalidatesTags: (_res, _err, { userId }) => [{ type: "User", id: userId }, "User"],
    }),
    updateUserLocation: builder.mutation<unknown, { userId: string; location: string }>({
      query: ({ userId, location }) => ({ url: `/users/${userId}/location`, method: "PATCH", data: { location } }),
      invalidatesTags: (_res, _err, { userId }) => [{ type: "User", id: userId }, "User"],
    }),
    updateProfileLocation: builder.mutation<unknown, { userId: string; location: Record<string, unknown> }>({
      query: ({ userId, location }) => ({
        url: `/users/${userId}/profile-location`,
        method: "PATCH",
        data: { location },
      }),
      invalidatesTags: (_res, _err, { userId }) => [{ type: "User", id: userId }, "User", "Auth"],
    }),
    updateBrowsingLocation: builder.mutation<
      unknown,
      { userId: string; location: Record<string, unknown>; radiusMiles?: number }
    >({
      query: ({ userId, location, radiusMiles }) => ({
        url: `/users/${userId}/browsing-location`,
        method: "PATCH",
        data: { location, radiusMiles },
      }),
      invalidatesTags: (_res, _err, { userId }) => [{ type: "User", id: userId }, "User", "Auth"],
    }),
    resetBrowsingLocation: builder.mutation<unknown, { userId: string; radiusMiles?: number }>({
      query: ({ userId, radiusMiles }) => ({
        url: `/users/${userId}/browsing-location/reset`,
        method: "POST",
        data: { radiusMiles },
      }),
      invalidatesTags: (_res, _err, { userId }) => [{ type: "User", id: userId }, "User", "Auth"],
    }),
    relocateUser: builder.mutation<unknown, { userId: string; payload: Record<string, unknown> }>({
      query: ({ userId, payload }) => ({ url: `/users/${userId}/relocate`, method: "POST", data: payload }),
      invalidatesTags: (_res, _err, { userId }) => [
        { type: "User", id: userId },
        "User",
        "Auth",
        { type: "Trades", id: "LIST" },
        { type: "Bounties", id: "LIST" },
        { type: "Listings", id: "LIST" },
        { type: "Listings", id: "MINE" },
      ],
    }),
  }),
});

export const {
  useSearchPublicUsersQuery,
  useFetchUserByIdQuery,
  useFetchPublicUserByUsernameQuery,
  useFetchPublicUserByIdQuery,
  useFetchUserAchievementsQuery,
  useUpdateUserAvatarMutation,
  useUpdateUserLocationMutation,
  useUpdateProfileLocationMutation,
  useUpdateBrowsingLocationMutation,
  useResetBrowsingLocationMutation,
  useRelocateUserMutation,
} = usersApi;
